/* Any non-catalogue page of the material: one JSON document, rendered.
   OWNED BY THE FOUNDATION.

   Every page that is not the catalogue — the theory, the five planning steps,
   production, the file maker, prompts, tools, take-away — is a document, so
   there is one route and one component for all of them. A new page is a JSON
   file, not a renderer, which is the whole point of moving the content to JSON.

   Three of them are made of named parts rather than a flat list of blocks; how
   those are presented is stated in the document as `layout`, not guessed from
   their shape. */

import { Link, useParams } from "react-router-dom";
import { Blocks } from "../blocks";
import { useI18n } from "../lib/i18n";
import { useFeature } from "../lib/portal";
import { useDoc } from "../lib/hooks";
import { Folds } from "./Folds";
import { Prompts } from "./Prompts";
import { LoadFailed } from "./NotFound";
import { Untranslated } from "./Untranslated";

export function DocPage({ id: fixed }: { id?: string } = {}) {
  const params = useParams();
  const id = fixed ?? params.id;
  const { t, ui, href } = useI18n();
  const deckOn = useFeature("deck");
  const { data: doc, error, loading } = useDoc(id);

  if (loading) return null;
  if (error || !doc) return <LoadFailed error={error} />;

  const parts = doc.parts ?? [];
  const presentable = deckOn && doc.layout === "slides" && parts.length > 0;

  return (
    <div className="catalog">
      <header className="cat-bill">
        <div className="cat-bill-in">
          {doc.eyebrow ? <div className="eyebrow">{t(doc.eyebrow)}</div> : null}
          <h1>{t(doc.title)}</h1>
          {doc.lead ? <p className="lead">{t(doc.lead)}</p> : null}
        </div>
      </header>

      <Untranslated doc={doc} />

      {presentable ? (
        <p className="doc-launch">
          <Link to={href(`/deck/${doc.id}/1`)}>{ui("deck.start")} →</Link>
        </p>
      ) : null}

      <div className="cat-content">
        {doc.blocks.length ? <Blocks blocks={doc.blocks} /> : null}

        {doc.layout === "prompts" ? <Prompts doc={doc} /> : null}

        {doc.layout !== "prompts" && parts.length ? (
          <Folds parts={parts}
                 numbered={doc.layout === "slides"}
                 heading={doc.layout === "slides" ? ui("doc.slides") : undefined}
                 deckHref={presentable ? (n) => href(`/deck/${doc.id}/${n}`) : undefined} />
        ) : null}
      </div>
    </div>
  );
}
